// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Catalog of every LaunchDarkly flag this app reads. One entry per flag.
 *
 * Adding a flagged surface:
 *   1. Add an entry here, `default: false`, so it only shows when the flag is
 *      explicitly `true` (absent, non-boolean or unconfigured stay hidden).
 *   2. Hide its nav entry or UI with `useFlagOn(name)` (hooks/useFlagOn.ts).
 *   3. Gate its route with `<FlagRoute flag={name} fallback={…}>`
 *      (components/common/FlagRoute.tsx). Both wait for LaunchDarkly to answer.
 *   Admin Center reaches both through `shared/featureFlags` (ADR-0014).
 *
 * Kill switches are the one exception to `default: false`: a screen that ships
 * by default and may need pulling in an emergency gets `default: true`, so an
 * unconfigured or unreachable LaunchDarkly leaves it visible and only an
 * explicit `false` hides it. Read those with `useFeatureFlag` (it returns the
 * default while flags load) and route them with `FlagRoute`.
 *
 * `useFeatureFlag()` (hooks/useFeatureFlag.ts) is the raw typed read the two
 * build on; components shouldn't need it.
 * Don't read `useFlags()` from the SDK directly outside this file and that
 * hook: a raw `flags['some-key']` lookup has no default, no type, and no
 * record of why the flag exists — this catalog is the fix for all three, and
 * it's what makes adding a flag anywhere else in the app a one-line job
 * instead of re-deriving the same boilerplate LaunchDarklyContext.tsx and
 * HeliosHeader.tsx used to have. The one sanctioned exception is
 * components/shell/FeatureFlagDebugPanel.tsx, which needs every entry's
 * value at once to render a live readout — see its own comment.
 *
 * Both shells share the site selection through shared/auth/useSiteSelection.
 * Full targeting model: the doc above buildContext in LaunchDarklyContext.tsx.
 */
export const LD_FLAGS = {
  adminCenterEnabled: {
    /** Exact key as created in LaunchDarkly — kebab-case, matches reactOptions.useCamelCaseFlagKeys: false. */
    key: 'admin-center-enabled',
    description: 'Shows the Admin Center nav entry and route. Stage-1 pilot, AB#29033.',
    default: false,
  },
  bulkImportEnabled: {
    key: 'bulk-import-enabled',
    description:
      'Shows Admin Center’s Bulk import nav item and route. Demonstrates the pattern gating one feature inside an already-gated surface, independent of admin-center-enabled.',
    default: false,
  },
  reviewQueueEnabled: {
    key: 'review-queue-enabled',
    description:
      'Shows Admin Center’s Review queue nav item and route (procedures/review-queue). Off by default: the page lists static placeholder rows and its Approve and Reject buttons do nothing, because SOP Factory has no approval route. The approval queue is deferred to GA-10 Part II (ADM-09, AB#33610).',
    default: false,
  },
  bulkImportUploadEnabled: {
    key: 'bulk-import-upload-enabled',
    description:
      'Shows the document dropzone at the top of Admin Center’s Bulk import page, independent of bulk-import-enabled. Hidden until uploads are wired to a pipeline service.',
    default: false,
  },
  accessManagementEnabled: {
    key: 'access-management-enabled',
    description:
      'Shows Admin Center’s Access section (Users + Roles) nav group and routes. Gated as one unit, independent of admin-center-enabled.',
    default: false,
  },
  notesAuditEnabled: {
    key: 'notes-audit-enabled',
    description:
      'Shows the Notes rail item and the Notes (/log) and Audit (/audit) pages. Hidden until they are ready, AB#33168.',
    default: false,
  },
  alarmLifecycleEnabled: {
    key: 'alarm-lifecycle-enabled',
    description:
      'Shows the operator actions card on Alarm detail (Acknowledge, work order create/complete/reopen, Force clear). Hidden until AEM supports the two-status lifecycle and the new events.',
    default: false,
  },
  fleetOverviewMetricsEnabled: {
    key: 'fleet-overview-metrics-enabled',
    description:
      'Shows the Fleet overview KPI tiles with no data source yet (IT capacity, Fleet availability, Fleet-level PUE) and the per-site capacity figure. Hidden until the backend provides them.',
    default: false,
  },
  planControlHandoffEnabled: {
    key: 'plan-control-handoff-enabled',
    description:
      'Shows the Control handoff (CSV to PMS) panel on the plan Audit tab. Its steps are placeholders until the handoff is integrated, so it stays hidden until then.',
    default: false,
  },
  routineTabEnabled: {
    key: 'routine-tab-enabled',
    description:
      'Shows the Routine tab on the alarms home and its /home/routine route. Hidden until it is ready, AB#33171.',
    default: false,
  },
  addProcedureEnabled: {
    key: 'add-procedure-enabled',
    description:
      'Shows Admin Center’s Add procedure nav item and route, independent of admin-center-enabled.',
    default: false,
  },
  adminRulesSummaryEnabled: {
    key: 'admin-rules-summary-enabled',
    description:
      'Shows the Rules summary on Admin Center Home. Hidden until the numbers come from the alarm rule catalogue instead of being hardcoded.',
    default: false,
  },
  adminProceduresSummaryEnabled: {
    key: 'admin-procedures-summary-enabled',
    description:
      'Shows the Procedures summary on Admin Center Home (library counts and the By system breakdown). Hidden for GA-10 until SOP content is approved and ADM serves it (AB#33812); the Library nav item and route are unaffected.',
    default: false,
  },
  procedureLibraryOwnerUpdatedEnabled: {
    key: 'procedure-library-owner-updated-enabled',
    description:
      'Shows the Owner and Updated columns on Admin Center’s procedure Library. Hidden until SOP Factory returns real values; today they are placeholders.',
    default: false,
  },
  workOrdersEnabled: {
    key: 'work-orders-enabled',
    description:
      'Kill switch for the Work orders board: its rail item and /work-orders route. On by default (every other flag here defaults off); turn it off in LaunchDarkly to hide the board, for example if the MaintainX production API key is not in PROD by Nov 4 (AB#33799).',
    default: true,
  },
  procedureSubmitEnabled: {
    key: 'procedure-submit-enabled',
    description:
      'Enables the Submit request button on an existing procedure (procedures/view/:artifactId). Off by default — SOP Factory’s catalog is read-only, so sopFactoryService.updateArtifact has no backend route and always 405s today (AB#34512). ProcedureViewPage shows the button disabled with an explanation instead of letting the request fail.',
    default: false,
  },
  siteProceduresEnabled: {
    key: 'site-procedures-enabled',
    description:
      'Shows the Procedures tab and route under a site (/site/:site_id/procedures), the SOP catalog. On by default; GA-10 may ship it hidden if ADM list + get is not in PROD in time (AB#33803). Turn it off in LaunchDarkly, or per environment with REACT_APP_FLAG_SITE_PROCEDURES_ENABLED=false, which is only a fallback: a value LaunchDarkly has for the flag wins over it.',
    default: true,
  },
  admSopCatalogEnabled: {
    key: 'adm-sop-catalog-enabled',
    description:
      'Reads SOPs (the Site Procedures list and the SOP detail page) from ADM, scoped to the selected site, instead of the global SOP Factory catalog. Off until ADM is deployed in the environment (ADM-01, Oct 30) and the BFF has ADM_SERVICE_URL set (AB#33803). A one-flag switch: turning it on in LaunchDarkly needs no deploy.',
    default: false,
  },
  alarmResponseProcedureEnabled: {
    key: 'alarm-response-procedure-enabled',
    description:
      'Shows the SOPs bound to an alarm or anomaly: the Response procedure card on Alarm detail and the linked-procedures list on Anomaly detail. On by default; GA-10 may ship it hidden until the bound SOPs come from ADM (AB#33794). Turn it off in LaunchDarkly, or per environment with REACT_APP_FLAG_ALARM_RESPONSE_PROCEDURE_ENABLED=false, which is only a fallback: a value LaunchDarkly has for the flag wins over it.',
    default: true,
  },
  alarmOwnerEnabled: {
    key: 'alarm-owner-enabled',
    description:
      'Shows the Owner row on the alarm Next step card, with Assign to me and Release. Off by default: it needs the BFF assignment route and AEM accepting assigned_to on the alarm in that environment. Turn it on in LaunchDarkly, or per environment with REACT_APP_FLAG_ALARM_OWNER_ENABLED=true, which is only a fallback: a value LaunchDarkly has for the flag wins over it.',
    default: false,
  },
} as const;

export type LDFlagName = keyof typeof LD_FLAGS;

/** Which of the user's sites the UI shows. `all` wins over `sites`. */
export interface SiteRollout {
  all: boolean;
  /** `site_id`s, the same ids as the roster and the `/site/:site_id` routes. */
  sites: readonly string[];
}

/**
 * The one JSON flag, kept out of `LD_FLAGS` so that stays boolean. Target it
 * by `organization.key`: the browser SDK evaluates one context at a time, so a
 * per-site boolean could only ever answer for the site already selected — a
 * list is what lets the switcher hide the others. Read it with
 * `useSiteRollout()`.
 *
 * UI rollout only, not API authorization. The default shows every site, so an
 * unreachable or unconfigured LaunchDarkly leaves the app as it was rather
 * than emptying it; hiding sites takes an explicit value from the dashboard.
 */
export const SITE_ROLLOUT_FLAG = {
  key: 'site-rollout',
  default: { all: true, sites: [] } as SiteRollout,
} as const;
